import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import { productionAPI } from '../api';
import toast from 'react-hot-toast';
import { confirmToast } from '../components/ConfirmToast';
import SuggestInput from '../components/SuggestInput';

// Сегодняшний день так, как его понимает <input type="date"> и сервер.
// toISOString() здесь нельзя: он переводит в UTC, и в Алматы вечером
// «сегодня» превратилось бы в завтра.
const todayISO = () => {
  const d = new Date();
  const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};

// Услуга: плиту привозит заказчик.
//
// Метка одна на все четыре документа — бланк, сменный лист, отчёт за смену и
// экран правки. Цвет берётся у текста, а не задан своим: бланк печатают
// чёрным по белому, и цветная плашка на бумаге станет серым пятном.
const SvcMark = () => (
  <span title="Услуга — плиту привозит заказчик"
    style={{
      border: '1px solid currentColor', borderRadius: 3,
      padding: '0 4px', marginLeft: 5, fontSize: '.78em',
      fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '.02em',
    }}>УСЛУГА</span>
);

const Btn = ({ children, variant = 'primary', size = 'sm', ...props }) => {
  const bg = {
    primary: 'var(--accent)', success: '#22c55e',
    danger: '#ef4444', ghost: 'transparent', warning: '#f59e0b'
  }[variant];
  const border = variant === 'ghost' ? '1px solid var(--border)' : 'none';
  return (
    <button
      {...props}
      style={{
        background: props.disabled ? 'var(--card)' : bg,
        border, borderRadius: 7,
        // ghost — прозрачная кнопка: белый текст на ней исчезал в светлой теме
        color: props.disabled ? 'var(--mid)' : (variant === 'ghost' ? 'var(--text)' : '#fff'),
        padding: size === 'sm' ? '6px 12px' : '10px 20px',
        fontSize: size === 'sm' ? 13 : 14,
        fontWeight: 600, cursor: props.disabled ? 'not-allowed' : 'pointer',
        ...props.style
      }}
    >
      {children}
    </button>
  );
};

// Дописать в смену позицию, которую ещё не согласовали. Источник тот же,
// что и у таблицы согласования, — несогласованные позиции заказов.
function AddRowModal({ onClose, onAdd, busy }) {
  const [rows, setRows]       = useState([]);
  const [presses, setPresses] = useState([]);
  const [sel, setSel]         = useState(new Set());
  const [pressId, setPressId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([productionAPI.getApprovalTable(), productionAPI.getPresses()])
      .then(([a, p]) => {
        setRows(a.data.rows || []);
        setPresses((p.data || []).filter(x => x.is_active));
      })
      .catch(() => toast.error(t('Не удалось загрузить список позиций')))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSel(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const TDx = { padding: '7px 8px', borderBottom: '1px solid var(--border)', fontSize: 13 };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--card)', borderRadius: 14, width: '100%', maxWidth: 820,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>
            {t('Добавить позицию в смену')}
          </h3>
          <select value={pressId} onChange={e => setPressId(e.target.value)}
            style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', padding: '6px 10px', fontSize: 13 }}>
            <option value="">{t('— пресс —')}</option>
            {presses.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ overflowY: 'auto', padding: '0 20px' }}>
          {loading ? (
            <p style={{ color: 'var(--muted)', padding: 20 }}>{t('Загрузка…')}</p>
          ) : rows.length === 0 ? (
            <p style={{ color: 'var(--muted)', padding: 20 }}>{t('Несогласованных позиций нет')}</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {rows.map(r => (
                  <tr key={r.order_item_id} style={{ opacity: r.marked ? 0.5 : 1 }}>
                    <td style={{ ...TDx, width: 34 }}>
                      <input type="checkbox" disabled={r.marked}
                        checked={sel.has(r.order_item_id)}
                        onChange={() => toggle(r.order_item_id)} />
                    </td>
                    <td style={TDx}>
                      <div style={{ color: 'var(--text)', fontWeight: 600 }}>{r.product_name}</div>
                      <div style={{ color: 'var(--dim)', fontSize: 11 }}>
                        {r.client_name} · {r.texture_name} · {r.order_number}
                      </div>
                    </td>
                    <td style={{ ...TDx, textAlign: 'right', fontWeight: 700, color: 'var(--text)' }}>
                      {r.quantity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10 }}>
          <Btn variant="success" disabled={busy || sel.size === 0 || !pressId}
            onClick={() => onAdd([...sel], pressId)}>
            {busy ? '…' : `＋ ${t('Добавить')} (${sel.size})`}
          </Btn>
          <Btn variant="ghost" onClick={onClose}>{t('Отмена')}</Btn>
          {!pressId && sel.size > 0 && (
            <span style={{ color: '#f59e0b', fontSize: 12, alignSelf: 'center' }}>
              {t('Выберите пресс')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// Правка — отдельная форма, а не бланк. В бланке клетки узкие и нет истории
// ввода; здесь те же поля, что и при согласовании заявки, с подсказками.
function RequestEditForm({ data, draft, setField, onRemove, onAdd, onSave, onCancel, busy }) {
  const TH = {
    padding: '8px', fontSize: 11, color: 'var(--muted)', textAlign: 'left',
    textTransform: 'uppercase', borderBottom: '1px solid var(--border)',
  };
  const TD = {
    padding: '7px 8px', fontSize: 13, color: 'var(--text)',
    borderBottom: '1px solid var(--border)', verticalAlign: 'top',
  };
  const numStyle = {
    width: '100%', boxSizing: 'border-box', padding: '4px 6px', fontSize: 12,
    background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 5, color: 'var(--text)',
  };

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <h3 style={{ color: 'var(--text)', fontSize: 15, fontWeight: 700 }}>
          ✏ {t('Изменение заявки')} {data.number}
        </h3>
        <span style={{ color: 'var(--muted)', fontSize: 12 }}>
          {t('Производитель ДСП и упаковку называет менеджер при заказе — здесь только то, чего он не назвал')}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 880 }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: 36 }}></th>
              <th style={{ ...TH, width: 96 }}>{t('Пресс')}</th>
              <th style={TH}>{t('Заказчик')}</th>
              <th style={TH}>{t('Наименование')}</th>
              <th style={{ ...TH, width: 110 }}>{t('Матрица')}</th>
              <th style={{ ...TH, width: 76 }}>{t('План')}</th>
              <th style={{ ...TH, width: 56 }}>{t('Факт')}</th>
              <th style={{ ...TH, width: 160 }}>{t('Производитель ДСП')}</th>
              <th style={{ ...TH, width: 180 }}>{t('Тип упаковки')}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => {
              const d = draft[r.id] || {};
              const locked = !r.can_edit;
              return (
                <tr key={r.id} style={{ opacity: locked ? 0.55 : 1 }}>
                  <td style={TD}>
                    {r.can_remove ? (
                      <button onClick={() => onRemove(r)} title={t('Убрать из смены')}
                        style={{
                          border: 'none', background: '#ef4444', color: '#fff',
                          borderRadius: 5, fontSize: 12, padding: '3px 7px', cursor: 'pointer',
                        }}>✖</button>
                    ) : locked ? (
                      <span title={r.closed ? t('Выпущено — строка закрыта')
                                            : t('Позиция в работе — менять нельзя')}>🔒</span>
                    ) : ''}
                  </td>
                  <td style={TD}>{r.press_name}</td>
                  <td style={TD}>{r.client_name}{r.is_service && <SvcMark />}</td>
                  <td style={TD}>{r.product_name}</td>
                  <td style={TD}>{r.texture_name}</td>
                  <td style={TD}>
                    {locked ? r.quantity_plan : (
                      <input type="number" min={r.quantity_fact || 0} style={numStyle}
                        value={d.quantity_plan ?? ''}
                        onChange={e => setField(r.id, 'quantity_plan', e.target.value)} />
                    )}
                  </td>
                  <td style={{ ...TD, fontWeight: 700 }}>{r.quantity_fact || 0}</td>
                  <td style={TD}>
                    {locked ? r.plate_size : (
                      <SuggestInput kind="plate_size" value={d.plate_size}
                        onChange={v => setField(r.id, 'plate_size', v)}
                        placeholder={t('размер')} />
                    )}
                  </td>
                  <td style={TD}>
                    {locked ? r.note : (
                      <SuggestInput kind="note" value={d.note}
                        onChange={v => setField(r.id, 'note', v)}
                        placeholder={t('примечание')} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <Btn variant="success" size="md" onClick={onSave} disabled={busy}>
          {busy ? '…' : `💾 ${t('Сохранить')}`}
        </Btn>
        <Btn variant="ghost" size="md" onClick={onAdd} disabled={busy}>
          ＋ {t('Добавить позицию')}
        </Btn>
        <Btn variant="ghost" size="md" onClick={onCancel} disabled={busy}>{t('Отмена')}</Btn>
      </div>
    </div>
  );
}

// Лист бланка на ОДИН пресс — смена получает на руки только свою работу.
// Ширины подобраны так, чтобы каждая строка умещалась в одну линию и таблица
// не росла вниз: служебные колонки узкие, «Наименование ЛДСП» — широкое.
// Ширина колонки «Заказчик» — под самое длинное имя ЭТОГО листа.
//
// Она стояла жёстко на 76 точках, и «Абдазимов Шухрат» превращался в
// «Абдазимов Шу…». Бумагу несут в цех: по ней собирают заказ и по ней же
// спрашивают, чьё это, — обрезанная фамилия там ничего не стоит.
//
// Считаем по числу символов, а не по факту отрисовки: при table-layout:fixed
// браузер ширину не подбирает, её задаёт первая строка шапки. Предел сверху
// нужен, чтобы одно длинное название не съело колонку с наименованием ЛДСП.
function clientColWidth(rows) {
  const longest = (rows || []).reduce(
    (m, r) => Math.max(m, String(r.client_name || '').length), 0);
  return Math.min(190, Math.max(76, Math.round(longest * 5.4) + 10));
}

function PrintSheet({ press, rows, data, dateStr, heads }) {
  const clientW = clientColWidth(rows);
  const TH = {
    border: '1px solid #000', padding: '2px 3px', fontSize: 8,
    fontWeight: 700, background: '#eee', color: '#000',
    textAlign: 'center', lineHeight: 1.1,
  };
  const TD = {
    border: '1px solid #000', padding: '2px 4px', fontSize: 9, color: '#000',
    lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  // Клетки под ручку: плёнка и разбраковка по сортам — система их не считает
  const HAND = { ...TD, width: 30 };
  const totalPlan = rows.reduce((s, r) => s + (r.quantity_plan || 0), 0);
  const totalFact = rows.reduce((s, r) => s + (r.quantity_fact || 0), 0);

  return (
    // Ширина листа = A4 landscape минус поля (297 - 2×7 мм ≈ 1069 px).
    // На экране бланк выглядит ровно так же, как ляжет на бумагу; в самом
    // документе печати эта ширина снимается (там лист задаёт @page).
    <div className="sheet" style={{
      background: '#fff', padding: 10, marginBottom: 18,
      width: 1069, maxWidth: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#000' }}>
            План производства {dateStr} — {press}
          </div>
          <div style={{ fontSize: 10, color: '#000', marginTop: 2 }}>
            время смены — {data.shift_display.toLowerCase()} · {data.number}
          </div>
          {data.is_cancelled && (
            <div style={{ color: '#c00', fontSize: 13, fontWeight: 700, letterSpacing: 2 }}>
              О Т М Е Н Е Н А{data.cancel_reason ? ` — ${data.cancel_reason}` : ''}
            </div>
          )}
        </div>
        {/* Ответственные — мелко, в правом верхнем углу, шапку не растягивают.
            Между должностью и фамилией пустая линия — там расписываются. */}
        <div style={{ fontSize: 9, color: '#000', lineHeight: 1.9, whiteSpace: 'nowrap' }}>
          {[
            ['Нач. производства', heads.prod_head],
            ['Бригадир',          heads.brigadier],
            ['Завсклад',          heads.warehouse_head],
          ].map(([label, name]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              <span style={{ width: 96 }}>{label}:</span>
              <span style={{ width: 74, borderBottom: '1px solid #000' }}></span>
              <b style={{ width: 96 }}>{name || ' '}</b>
            </div>
          ))}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            {/* При table-layout:fixed ширину задаёт ПЕРВАЯ строка шапки —
                поэтому объединённым «ЛДСП» и «ДСП некондиция» ширина нужна
                тоже, иначе они забирают всё свободное место. Без ширины
                остаётся только «Наименование ЛДСП» — ему и достаётся остаток. */}
            <th rowSpan={2} style={{ ...TH, width: 22 }}>№</th>
            <th rowSpan={2} style={{ ...TH, width: clientW }}>Заказчик</th>
            <th rowSpan={2} style={TH}>Наименование ЛДСП</th>
            <th rowSpan={2} style={{ ...TH, width: 62 }}>Произв.<br />ДСП</th>
            <th rowSpan={2} style={{ ...TH, width: 96 }}>Матрица</th>
            <th rowSpan={2} style={{ ...TH, width: 38 }}>кол-во<br />план</th>
            <th rowSpan={2} style={{ ...TH, width: 38 }}>кол-во<br />факт</th>
            <th rowSpan={2} style={{ ...TH, width: 32 }}>кол-во<br />пленк</th>
            <th colSpan={2} style={{ ...TH, width: 56 }}>ЛДСП</th>
            <th colSpan={2} style={{ ...TH, width: 56 }}>ДСП некондиция</th>
            <th rowSpan={2} style={{ ...TH, width: 32 }}>Пленки<br />брак</th>
            <th rowSpan={2} style={{ ...TH, width: 72 }}>Тип<br />упаковки</th>
          </tr>
          <tr>
            {/* Сорта ЛДСП — по счёту заказчика: 1-й это факт, здесь то, что
                хуже. ДСП некондиция — сорт ПЛИТЫ от поставщика, он свой. */}
            <th style={{ ...TH, width: 28 }}>2-й сорт</th>
            <th style={{ ...TH, width: 28 }}>3-й сорт</th>
            <th style={{ ...TH, width: 28 }}>1 сорт</th>
            <th style={{ ...TH, width: 28 }}>2 сорт</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const prev = i > 0 ? rows[i - 1] : null;
            const showDiv = prev && prev.texture_name !== r.texture_name;
            return (
              <React.Fragment key={r.id}>
                {showDiv && (
                  <tr>
                    <td colSpan={15} style={{
                      ...TD, fontWeight: 700, textAlign: 'center', background: '#f2f2f2',
                    }}>
                      замена матрицы &raquo; {r.texture_name}
                    </td>
                  </tr>
                )}
                <tr>
                  <td style={{ ...TD, textAlign: 'center' }}>{i + 1}</td>
                  <td style={TD}>{r.client_name}{r.is_service && <SvcMark />}</td>
                  {/* Название бывает длинным — разрешаем перенос */}
                  <td style={{ ...TD, whiteSpace: 'normal' }}>{r.product_name}</td>
                  <td style={TD}>{r.plate_size}</td>
                  <td style={TD}>{r.texture_name}</td>
                  <td style={{ ...TD, textAlign: 'center', fontWeight: 700 }}>{r.quantity_plan}</td>
                  <td style={{ ...TD, textAlign: 'center' }}>{r.quantity_fact || ''}</td>
                  {/* Под ручку: плёнка, ЛДСП 2-й/3-й сорт, ДСП 1/2 сорт, брак плёнки */}
                  <td style={HAND}></td>
                  <td style={HAND}></td>
                  <td style={HAND}></td>
                  <td style={HAND}></td>
                  <td style={HAND}></td>
                  <td style={HAND}></td>
                  <td style={{ ...TD, whiteSpace: 'normal' }}>{r.note}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr>
            <td colSpan={5} style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>Итого</td>
            <td style={{ ...TD, textAlign: 'center', fontWeight: 700 }}>{totalPlan}</td>
            <td style={{ ...TD, textAlign: 'center', fontWeight: 700 }}>{totalFact || ''}</td>
            <td colSpan={7} style={TD}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ── Отчёт за день ─────────────────────────────────────────────────────────────
// Технолог раздаёт заявки кусками, по мере согласования заказов — к концу
// смены их накапливается десяток, и общей картины по ним не видно. Здесь всё,
// что за день ушло в производство, одним листом: по сменам, внутри — по
// прессам. Одинаковый продукт складывается в одну строку.
//
// Брак тут проставлен системой (в сменном бланке те же клетки пустые, под
// ручку): отчёт читают уже после смены, и цифры должны быть настоящими.
// Плёнка меряется в метрах и в штуках: где-то целое, где-то «12,5».
// Показываем как есть, без лишнего хвоста нулей.
function fmtFilm(v) {
  const n = Number(v || 0);
  if (!n) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ',');
}

function DailyReportSheet({ date, shift, press, byBrigadier }) {
  // Та же колонка и в отчёте за смену: имена в ней те же самые
  const allRows = (press.groups || []).reduce((a, g) => a.concat(g.rows || []), []);
  const clientW = clientColWidth(allRows);
  const TH = {
    border: '1px solid #000', padding: '2px 3px', fontSize: 8,
    fontWeight: 700, background: '#eee', color: '#000',
    textAlign: 'center', lineHeight: 1.1,
  };
  const TD = {
    border: '1px solid #000', padding: '2px 4px', fontSize: 9, color: '#000',
    lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };
  const NUM = { ...TD, textAlign: 'center' };
  const d = (row, name) => row.defects[name] || '';
  // Итог по графам сортов и брака. Строки эти считают глазами и сверяют с
  // тем, что принял склад: без итога приходится складывать в столбик прямо
  // на бланке, а ошибка в сложении — это спор с цехом на следующий день.
  const sumD = (rows, name) => {
    const n = rows.reduce((a, r) => a + (Number(r.defects[name]) || 0), 0);
    return n || '';
  };

  return (
    <div className="sheet" style={{
      background: '#fff', padding: 10, marginBottom: 18, width: 1069, maxWidth: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#000' }}>
            Отчёт за смену {date} — {shift.shift_display.toLowerCase()} — {press.press}
          </div>
          <div style={{ fontSize: 10, color: '#000', marginTop: 2 }}>
            заявки: {shift.numbers.join(', ')}
          </div>
        </div>
        <div style={{ fontSize: 9, color: '#000', lineHeight: 1.9, whiteSpace: 'nowrap' }}>
          {[
            ['Нач. производства', shift.prod_heads],
            ['Бригадир',          shift.brigadiers],
            ['Завсклад',          shift.warehouse_heads],
          ].map(([label, names]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
              <span style={{ width: 96 }}>{label}:</span>
              <span style={{ width: 74, borderBottom: '1px solid #000' }}></span>
              <b style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {(names || []).join(', ') || ' '}
              </b>
            </div>
          ))}
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th rowSpan={2} style={{ ...TH, width: 22 }}>№</th>
            <th rowSpan={2} style={{ ...TH, width: clientW }}>Заказчик</th>
            {/* Толщина — раньше названия: по ней берут плиту со стеллажа, и
                искать её в хвосте «…2750*1830*16 мм» глазами по всему листу
                неудобно. Своей графой она читается сразу, сверху вниз. */}
            <th rowSpan={2} style={{ ...TH, width: 34 }}>Толщ.<br />мм</th>
            <th rowSpan={2} style={TH}>Наименование ЛДСП</th>
            <th rowSpan={2} style={{ ...TH, width: 62 }}>Произв.<br />ДСП</th>
            <th rowSpan={2} style={{ ...TH, width: 96 }}>Матрица</th>
            <th rowSpan={2} style={{ ...TH, width: 38 }}>кол-во<br />план</th>
            {/* Факт — это 1-й сорт. Второй и третий стоят в своих графах
                справа: складывать их сюда значит показывать 1-го больше,
                чем лежит на складе. */}
            <th rowSpan={2} style={{ ...TH, width: 38 }}>факт<br />1-й сорт</th>
            {/* Сколько плёнки ушло на выпущенные листы — по рецепту:
                двусторонний берёт две, односторонний одну. Брак плёнки сюда
                не входит, у него своя графа справа. */}
            <th rowSpan={2} style={{ ...TH, width: 38 }}>Пленка<br />расход</th>
            <th colSpan={3} style={{ ...TH, width: 84 }}>ЛДСП</th>
            <th rowSpan={2} style={{ ...TH, width: 38 }}>Пленки<br />брак</th>
            <th rowSpan={2} style={{ ...TH, width: 38 }}>ДСП<br />брак</th>
            <th rowSpan={2} style={{ ...TH, width: 72 }}>Тип<br />упаковки</th>
          </tr>
          <tr>
            <th style={{ ...TH, width: 28 }}>2-й сорт</th>
            <th style={{ ...TH, width: 28 }}>3-й сорт</th>
            {/* Пока смену не приняли, сорта ещё нет: оператор назвал только
                число не 1-х, а какой это сорт — решит склад. Без этой
                графы такая смена стояла в сводке с пустыми сортами, будто
                у неё всё вышло 1-м. */}
            <th style={{ ...TH, width: 28 }}>не 1-й<br />(пресс)</th>
          </tr>
        </thead>
        <tbody>
          {press.groups.map(g => {
            let n = 0;
            return (
              <React.Fragment key={g.brigadier || 'all'}>
                {byBrigadier && (
                  <tr>
                    <td colSpan={14} style={{
                      ...TD, fontWeight: 700, background: '#f2f2f2',
                    }}>
                      Бригадир: {g.brigadier || '— не указан —'}
                    </td>
                  </tr>
                )}
                {g.rows.map((r, i) => {
                  n += 1;
                  // Смена матрицы — остановка пресса: её ставят между позициями
                  // и по ней считают время. В самой заявке строка есть, а в
                  // отчёте за смену её не было — и порядок работы по бумаге
                  // читался как сплошной, без единой переналадки.
                  const prev = i > 0 ? g.rows[i - 1] : null;
                  const showDiv = prev && prev.texture_name !== r.texture_name;
                  return (
                    <React.Fragment key={`${g.brigadier}-${n}`}>
                    {showDiv && (
                      <tr>
                        <td colSpan={14} style={{
                          ...TD, fontWeight: 700, textAlign: 'center', background: '#f2f2f2',
                        }}>
                          замена матрицы &raquo; {r.texture_name}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={NUM}>{n}</td>
                      <td style={{ ...TD, whiteSpace: 'normal' }}>{r.client_name}{r.is_service && <SvcMark />}</td>
                      <td style={{ ...NUM, fontWeight: 700 }}>{r.thickness}</td>
                      <td style={{ ...TD, whiteSpace: 'normal' }}>{r.product_name}</td>
                      <td style={TD}>{r.plate_size}</td>
                      <td style={TD}>{r.texture_name}</td>
                      <td style={{ ...NUM, fontWeight: 700 }}>{r.quantity_plan}</td>
                      <td style={NUM}>{r.quantity_fact || ''}</td>
                      <td style={NUM}>{fmtFilm(r.film_fact)}</td>
                      <td style={NUM}>{d(r, '2-й сорт')}</td>
                      <td style={NUM}>{d(r, '3-й сорт')}</td>
                      <td style={NUM}>{d(r, 'Не 1-й сорт')}</td>
                      <td style={NUM}>{d(r, 'Брак плёнки')}</td>
                      <td style={NUM}>{d(r, 'Брак ДСП')}</td>
                      <td style={{ ...TD, whiteSpace: 'normal' }}>{r.note}</td>
                    </tr>
                    </React.Fragment>
                  );
                })}
                {byBrigadier && (
                  <tr>
                    <td colSpan={6} style={{ ...TD, textAlign: 'right' }}>Итого по бригадиру</td>
                    <td style={{ ...NUM, fontWeight: 700 }}>{g.total_plan}</td>
                    <td style={{ ...NUM, fontWeight: 700 }}>{g.total_fact || ''}</td>
                    <td style={{ ...NUM, fontWeight: 700 }}>{fmtFilm(g.total_film)}</td>
                    {['2-й сорт', '3-й сорт', 'Не 1-й сорт', 'Брак плёнки', 'Брак ДСП']
                      .map(name => (
                        <td key={name} style={{ ...NUM, fontWeight: 700 }}>
                          {sumD(g.rows, name)}
                        </td>
                      ))}
                    <td style={TD}></td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          <tr>
            <td colSpan={6} style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>
              Итого по прессу
            </td>
            <td style={{ ...NUM, fontWeight: 700 }}>{press.total_plan}</td>
            <td style={{ ...NUM, fontWeight: 700 }}>{press.total_fact || ''}</td>
            <td style={{ ...NUM, fontWeight: 700 }}>{fmtFilm(press.total_film)}</td>
            {['2-й сорт', '3-й сорт', 'Не 1-й сорт', 'Брак плёнки', 'Брак ДСП']
              .map(name => (
                <td key={name} style={{ ...NUM, fontWeight: 700 }}>
                  {sumD(allRows, name)}
                </td>
              ))}
            <td style={TD}></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function DailyReportView({ onBack }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]   = useState(today);
  const [byBrig, setByBrig] = useState(false);
  const [byShift, setByShift] = useState(true);
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    productionAPI.getDailyReport({ date, by_brigadier: byBrig ? '1' : '0' })
      .then(r => setData(r.data))
      .catch(() => toast.error(t('Не удалось загрузить отчёт')))
      .finally(() => setLoading(false));
  }, [date, byBrig]);

  const printReport = () => {
    const node = document.getElementById('daily-print');
    if (!node) return;
    // Заголовок пустой намеренно: браузер печатает его как колонтитул.
    // Вместе с margin:0 у @page колонтитулов не остаётся вовсе — ни названия
    // сверху, ни адреса страницы снизу. Поля рисуем сами, отступом листа.
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title></title>
<style>
  @page { size: A4 landscape; margin: 0; }
  html, body { margin:0; padding:0; background:#fff; color:#000;
               font-family: Arial, Helvetica, sans-serif; }
  table { width:100%; border-collapse:collapse; }
  thead { display:table-header-group; }
  tr { page-break-inside:avoid; }
  /* Поля листа: сверху 20 мм, по бокам 15 мм, снизу 12 мм */
  .sheet { width:auto !important; max-width:none !important;
           padding: 20mm 15mm 12mm !important; box-sizing: border-box;
           min-height: 210mm; page-break-after: always; }
  .sheet:last-child { page-break-after: auto; }
  /* Полоса смены — только для экрана. На бумаге плотная заливка ложится
     грязным пятном, а смена и так стоит в заголовке каждого листа:
     «Отчёт за смену … — ночь — Пресс №3». */
  .shift-band { display: none !important; }
</style></head><body>${node.innerHTML}</body></html>`;
    // Печать в отдельном документе: нулевой iframe печатает пустую страницу
    const f = document.createElement('iframe');
    f.style.cssText = 'position:fixed;left:-10000px;top:0;width:1123px;height:794px;border:0;';
    document.body.appendChild(f);
    f.contentDocument.open(); f.contentDocument.write(doc); f.contentDocument.close();
    setTimeout(() => {
      f.contentWindow.focus(); f.contentWindow.print();
      setTimeout(() => document.body.removeChild(f), 1000);
    }, 250);
  };

  const raw = (data && data.shifts) || [];

  // Смены сводим в одну, когда деление не нужно: прессы с одинаковым именем
  // складываются, чтобы на лист снова пришлись сутки целиком.
  const merge = (list) => {
    if (!list.length) return [];
    const byPress = {};
    const numbers = [];
    list.forEach(sh => {
      (sh.numbers || []).forEach(n => { if (!numbers.includes(n)) numbers.push(n); });
      (sh.presses || []).forEach(p => {
        const cur = byPress[p.press] || {
          press: p.press, groups: [], total_plan: 0, total_fact: 0, total_film: 0 };
        cur.groups = cur.groups.concat(p.groups || []);
        cur.total_plan += p.total_plan || 0;
        cur.total_fact += p.total_fact || 0;
        cur.total_film += p.total_film || 0;
        byPress[p.press] = cur;
      });
    });
    const pick = (key) => {
      const out = [];
      list.forEach(sh => (sh[key] || []).forEach(v => {
        if (v && !out.includes(v)) out.push(v);
      }));
      return out;
    };
    return [{
      shift: 'all', shift_display: 'смены вместе', numbers,
      presses: Object.values(byPress).sort((a, b) => a.press.localeCompare(b.press)),
      prod_heads: pick('prod_heads'), brigadiers: pick('brigadiers'),
      warehouse_heads: pick('warehouse_heads'),
    }];
  };

  // День всегда первым: так его читают и так идёт работа в сутках.
  const ORDER = { day: 0, night: 1 };
  const shifts = byShift
    ? [...raw].sort((a, b) => (ORDER[a.shift] ?? 9) - (ORDER[b.shift] ?? 9))
    : merge(raw);

  return (
    <div>
      <div className="no-print" style={{
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap',
      }}>
        <Btn variant="ghost" onClick={onBack}>← {t('К заявкам')}</Btn>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{
            padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 7, color: 'var(--text)', fontSize: 13,
          }} />
        {/* Разбивка по бригадирам — по требованию: обычно она не нужна,
            бригадиры смены и так стоят в шапке. */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 7,
          color: 'var(--muted)', fontSize: 13, cursor: 'pointer',
        }}>
          <input type="checkbox" checked={byBrig} onChange={e => setByBrig(e.target.checked)} />
          {t('Разбить по бригадирам')}
        </label>
        {/* День и ночь — разные бригады и разный спрос. Но иногда нужен один
            лист на пресс за сутки: тогда смены сводим вместе, как было
            раньше. */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 7,
          color: 'var(--muted)', fontSize: 13, cursor: 'pointer',
        }}>
          <input type="checkbox" checked={byShift}
            onChange={e => setByShift(e.target.checked)} />
          {t('Разделять по сменам')}
        </label>
        <Btn variant="primary" onClick={printReport} disabled={!shifts.length}
          style={{ marginLeft: 'auto' }}>🖨 {t('Печать A4')}</Btn>
      </div>

      {loading ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>{t('Загрузка…')}</p>
      ) : !shifts.length ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>
          {t('За этот день заявок нет')}
        </p>
      ) : (
        <div id="daily-print" style={{ overflowX: 'auto' }}>
          {shifts.map(sh => (
            <React.Fragment key={sh.shift}>
              {/* Полоса смены над её прессами: лист пресса читают отдельно, и
                  без неё непонятно, к какой бригаде он относится. При печати
                  полоса уходит наверх новой страницы вместе с первым листом. */}
              {byShift && (
                <div className="shift-band" style={{
                  background: sh.shift === 'night' ? '#312e81' : '#166534',
                  color: '#fff', borderRadius: 8, padding: '10px 16px',
                  margin: '18px 0 10px', display: 'flex', gap: 14,
                  alignItems: 'center', flexWrap: 'wrap',
                }}>
                  <span style={{ fontSize: 17, fontWeight: 800 }}>
                    {sh.shift === 'night' ? '🌙' : '☀️'} {sh.shift_display.toUpperCase()}
                  </span>
                  <span style={{ fontSize: 13, opacity: .85 }}>
                    {(sh.presses || []).length} {t('пресса')} ·{' '}
                    {t('план')} {sh.total_plan} · {t('факт')} {sh.total_fact}
                  </span>
                  <span style={{ fontSize: 12, opacity: .75, marginLeft: 'auto' }}>
                    {(sh.numbers || []).join(', ')}
                  </span>
                </div>
              )}
              {sh.presses.map(p => (
                <DailyReportSheet key={`${sh.shift}-${p.press}`}
                  date={date} shift={sh} press={p} byBrigadier={data.by_brigadier} />
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Сменный бланк пресса ─────────────────────────────────────────────────────
//
// Тот самый лист, который операторы заполняли от руки на следующий день. Все
// графы, кроме паллеты плёнки, оператор уже вводил при старте смены — режим
// пресса, микроклимат цеха, партию ДСП. Печатаем то, что есть, вместо второго
// переписывания.
//
// Лист на пресс за сутки: пресс — это станок, и спрашивают с него за день
// целиком. Смена стоит колонкой в строке, так ночь и день видно рядом.
// Строка = одна сессия: складывать их нельзя, весь смысл бланка в том, чтобы
// по браку вернуться к режиму, на котором лист прессовали.
function ShiftBlankSheet({ sheet }) {
  const TH = {
    border: '1px solid #000', padding: '3px 3px', fontSize: 8,
    fontWeight: 700, background: '#eee', color: '#000',
    textAlign: 'center', lineHeight: 1.15,
  };
  const TD = {
    border: '1px solid #000', padding: '3px 4px', fontSize: 9, color: '#000',
    lineHeight: 1.2,
  };
  const NUM = { ...TD, textAlign: 'center' };
  const HEAD = {
    border: '1px solid #000', padding: '4px 6px', fontSize: 10,
    color: '#000', textAlign: 'center', lineHeight: 1.3,
  };

  const rows = sheet.rows || [];

  return (
    <div className="sheet" style={{
      background: '#fff', padding: 10, marginBottom: 18, width: 1069, maxWidth: '100%',
    }}>
      {/* Шапка — та же, что на бумажном бланке */}
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', marginBottom: 4 }}>
        <tbody>
          <tr>
            <td style={{ ...HEAD, width: 90, fontWeight: 700 }}>
              Формат ДСП<br />{sheet.format || '—'}
            </td>
            <td style={{ ...HEAD, fontWeight: 700 }}>
              Бригадир ФИО {sheet.brigadier || '____________________'}
            </td>
            <td style={{ ...HEAD, width: 150, fontWeight: 700 }}>
              Пресс № {sheet.press}
            </td>
            <td style={{ ...HEAD, width: 110, fontWeight: 700 }}>
              Дата<br />{sheet.date}
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 40 }}>Смена</th>
            <th style={{ ...TH, width: 34 }}>Толщи&shy;на</th>
            <th style={TH}>Декор \ № паллеты плёнки</th>
            <th style={{ ...TH, width: 52 }}>Время лам&shy;и в сек.</th>
            <th style={{ ...TH, width: 86 }}>№ партии и дата произв. ДСП</th>
            <th style={{ ...TH, width: 60 }}>Темп. плит верх\низ</th>
            <th style={{ ...TH, width: 92 }}>Матрица</th>
            <th style={{ ...TH, width: 46 }}>Давл. плиты МПа</th>
            <th style={{ ...TH, width: 40 }}>Темп. цеха</th>
            <th style={{ ...TH, width: 40 }}>Влаж&shy;ность</th>
            <th style={{ ...TH, width: 44 }}>Кол-во, шт</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={NUM}>{r.shift_display}</td>
              <td style={NUM}>{r.thickness}</td>
              <td style={TD}>
                {r.decor}
                {r.is_service && <SvcMark />}
                {/* Паллета плёнки: у старых смен её не спрашивали — графа
                    остаётся пустой под ручку, как на прежнем бланке */}
                {r.film_pallet
                  ? <b style={{ marginLeft: 6 }}>№ {r.film_pallet}</b>
                  : <span style={{ marginLeft: 6, color: '#888' }}>\ ______</span>}
              </td>
              <td style={NUM}>{r.hold}</td>
              <td style={NUM}>{r.dsp_batch}</td>
              <td style={NUM}>{r.temperature}</td>
              <td style={{ ...TD, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.matrix}</td>
              <td style={NUM}>{r.pressure}</td>
              <td style={NUM}>{r.air_temp}</td>
              <td style={NUM}>{r.humidity}</td>
              <td style={{ ...NUM, fontWeight: 700 }}>{r.quantity}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={10} style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>Итого</td>
            <td style={{ ...NUM, fontWeight: 700 }}>{sheet.total}</td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: 10, display: 'flex', gap: 40, fontSize: 9, color: '#000' }}>
        {['Оператор', 'Бригадир', 'Нач. производства'].map(x => (
          <div key={x} style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
            <span>{x}:</span>
            <span style={{ width: 120, borderBottom: '1px solid #000' }}></span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShiftBlankView({ onBack }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]       = useState(today);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    productionAPI.getShiftBlank({ date })
      .then(r => setData(r.data))
      .catch(() => toast.error(t('Не удалось загрузить бланк')))
      .finally(() => setLoading(false));
  }, [date]);

  const printBlank = () => {
    const node = document.getElementById('blank-print');
    if (!node) return;
    // Пустой title намеренно: браузер печатает его колонтитулом. Вместе с
    // margin:0 у @page колонтитулов не остаётся — поля рисуем сами.
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title></title>
<style>
  @page { size: A4 landscape; margin: 0; }
  html, body { margin:0; padding:0; background:#fff; color:#000;
               font-family: Arial, Helvetica, sans-serif; }
  table { width:100%; border-collapse:collapse; }
  thead { display:table-header-group; }
  tr { page-break-inside:avoid; }
  .sheet { width:auto !important; max-width:none !important;
           padding: 18mm 12mm 12mm !important; box-sizing: border-box;
           min-height: 190mm; page-break-after: always; }
  .sheet:last-child { page-break-after: auto; }
</style></head><body>${node.innerHTML}</body></html>`;
    const f = document.createElement('iframe');
    f.style.cssText = 'position:fixed;left:-10000px;top:0;width:1123px;height:794px;border:0;';
    document.body.appendChild(f);
    f.contentDocument.open(); f.contentDocument.write(doc); f.contentDocument.close();
    setTimeout(() => {
      f.contentWindow.focus(); f.contentWindow.print();
      setTimeout(() => document.body.removeChild(f), 1000);
    }, 250);
  };

  const sheets = (data && data.sheets) || [];

  return (
    <div>
      <div className="no-print" style={{
        display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap',
      }}>
        <Btn variant="ghost" onClick={onBack}>← {t('К заявкам')}</Btn>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{
            padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 7, color: 'var(--text)', fontSize: 13,
          }} />
        <span style={{ color: 'var(--dim)', fontSize: 12.5 }}>
          {sheets.length ? `${sheets.length} ${t('бланк(ов)')}` : ''}
        </span>
        <Btn variant="primary" onClick={printBlank} disabled={!sheets.length}
          style={{ marginLeft: 'auto' }}>🖨 {t('Печать A4')}</Btn>
      </div>

      {loading ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>{t('Загрузка…')}</p>
      ) : !sheets.length ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>
          {t('За этот день смен на прессах не было')}
        </p>
      ) : (
        <div id="blank-print" style={{ overflowX: 'auto' }}>
          {sheets.map((sh, i) => (
            <ShiftBlankSheet key={`${sh.press}-${i}`} sheet={sh} />
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════
// ЗАЯВКА НА ПРОИЗВОДСТВО (Лист2)
//
// Список заявок по датам; открыл — увидел ту же таблицу, что согласовывал,
// и печатаешь на А4. Смену ставит система (ночь 18:00-07:00), бригадира
// вписывают руками уже здесь.
// «Кол-во факт» живое — сколько оператор реально выпустил.
// ═══════════════════════════════════════════════════════════════════════

function RequestPrintView({ requestId, onBack }) {
  const [data, setData]   = useState(null);
  const [heads, setHeads] = useState({ prod_head: '', brigadier: '', warehouse_head: '' });
  const [saving, setSaving] = useState(false);
  // Правка строк: черновик держим отдельно, чтобы «Отмена» ничего не портила
  const [editing, setEditing] = useState(false);
  const [adding, setAdding]   = useState(false);
  const [draft, setDraft]     = useState({});
  const [busy, setBusy]       = useState(false);

  const load = useCallback(() => {
    productionAPI.getRequest(requestId)
      .then(r => {
        setData(r.data);
        setHeads({
          prod_head:      r.data.prod_head || '',
          brigadier:      r.data.brigadier || '',
          warehouse_head: r.data.warehouse_head || '',
        });
      })
      .catch(() => toast.error(t('Не удалось загрузить заявку')));
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const act = async (action, payload) => {
    setBusy(true);
    try {
      const { data: d } = await productionAPI.editRequest(requestId, { action, ...payload });
      setData(d);
      return d;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Ошибка');
      return null;
    } finally { setBusy(false); }
  };

  const startEdit = () => {
    const next = {};
    (data.rows || []).forEach(r => {
      if (r.can_edit) next[r.id] = {
        plate_size: r.plate_size, note: r.note,
        quantity_plan: String(r.quantity_plan),
      };
    });
    setDraft(next);
    setEditing(true);
  };

  const cancelRequest = async () => {
    // Причина уходит в архив: потом видно, почему смену переиграли
    const reason = window.prompt(t('Причина отмены (необязательно):'), '');
    if (reason === null) return;
    const d = await act('cancel_request', { reason });
    if (d) toast.success(t('Заявка отменена, позиции вернулись в согласование'));
  };

  const restoreRequest = async () => {
    const d = await act('restore_request', {});
    if (d) toast.success(t('Заявка восстановлена'));
  };

  const removeRow = async (row) => {
    const ok = await confirmToast(
      `Убрать «${row.product_name}» из смены? Позиция вернётся в согласование.`,
      { danger: true, confirmText: 'Убрать' });
    if (!ok) return;
    const d = await act('remove', { item_id: row.id });
    if (d) toast.success(t('Позиция убрана'));
  };

  const setField = (id, key, val) =>
    setDraft(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [key]: val } }));

  const saveEdit = async () => {
    const items = (data.rows || [])
      .filter(r => draft[r.id])
      .filter(r => draft[r.id].plate_size !== r.plate_size
                || draft[r.id].note !== r.note
                || Number(draft[r.id].quantity_plan) !== r.quantity_plan)
      .map(r => ({
        id: r.id,
        plate_size: draft[r.id].plate_size,
        note: draft[r.id].note,
        quantity_plan: Number(draft[r.id].quantity_plan) || 0,
      }));
    if (items.length === 0) { setEditing(false); return; }

    const d = await act('save', { items });
    if (!d) return;
    const res = d.result || {};
    toast.success(t('Сохранено'));
    if (res.skipped && res.skipped.length) {
      toast.error(`Не изменено ${res.skipped.length}: ${res.skipped[0].why}`);
    }
    setEditing(false);
  };

  const saveHeads = async () => {
    setSaving(true);
    try {
      const { data: d } = await productionAPI.updateRequest(requestId, heads);
      setData(d);
      toast.success(t('Сохранено'));
    } catch { toast.error(t('Не удалось сохранить')); }
    finally { setSaving(false); }
  };

  // Печать в ОТДЕЛЬНОМ документе. Раньше печатали сам экран через
  // visibility:hidden — из-за этого тёмный фон уходил в принтер, а всё, что
  // не влезло на первую страницу, обрезалось. Здесь чистый белый документ
  // со своим @page: шапка повторяется, строки не рвутся, страниц столько,
  // сколько нужно.
  const printBlank = () => {
    const node = document.getElementById('req-print');
    if (!node) return;
    const doc = `<!doctype html><html><head><meta charset="utf-8"><title>${data.number}</title>
<style>
  @page { size: A4 landscape; margin: 7mm; }
  html, body { margin:0; padding:0; background:#fff; color:#000;
               font-family: Arial, Helvetica, sans-serif; }
  table { width:100%; border-collapse:collapse; }
  thead { display:table-header-group; }
  tr { page-break-inside:avoid; }
  /* На экране лист имеет ширину A4; на бумаге её задаёт @page */
  .sheet { width:auto !important; max-width:none !important; padding:0 !important;
           page-break-after: always; }
  .sheet:last-child { page-break-after: auto; }
</style></head><body>${node.innerHTML}</body></html>`;

    // Рамку прячем ЗА экран, но с реальным размером листа: у нулевого iframe
    // нечего верстать — браузер печатал пустую страницу и брал ориентацию
    // родительской вкладки, а не наш @page.
    const frame = document.createElement('iframe');
    frame.style.cssText =
      'position:fixed;left:-10000px;top:0;width:1123px;height:794px;border:0;';
    document.body.appendChild(frame);

    const fdoc = frame.contentWindow.document;
    fdoc.open();
    fdoc.write(doc);
    fdoc.close();

    // Даём вёрстке устояться, иначе печать может уйти по пустому документу
    setTimeout(() => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch { toast.error(t('Не удалось открыть печать')); }
      setTimeout(() => {
        if (document.body.contains(frame)) document.body.removeChild(frame);
      }, 2000);
    }, 250);
  };

  if (!data) return <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>{t('Загрузка…')}</p>;

  // На бумаге стоит день, НА КОТОРЫЙ работа выписана, а не день печати.
  // Бланк несут в цех и работают по нему: дата в шапке должна называть смену,
  // а не типографию. У заявок, выписанных до появления поля, дня нет — там
  // это одно и то же число.
  const dateStr = new Date(data.plan_date || data.created_at).toLocaleDateString('ru');

  // Лист на каждый пресс: порядок прессов — как в заявке, строки внутри
  // тоже сохраняют свой порядок
  const pressGroups = [];
  (data.rows || []).forEach(r => {
    const key = r.press_name || '—';
    let g = pressGroups.find(x => x.press === key);
    if (!g) { g = { press: key, rows: [] }; pressGroups.push(g); }
    g.rows.push(r);
  });

  return (
    <div>
      <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <Btn variant="ghost" onClick={onBack}>← {t('К списку')}</Btn>
        <span style={{ color: 'var(--muted)', fontSize: 13 }}>
          {data.number} · {data.shift_display} · {t('позиций:')} {data.rows.length}
        </span>
        {/* День, НА КОТОРЫЙ выписана заявка. Планы двигаются: понедельничную
            работу решают сделать в воскресенье. Пока переносить было нечем,
            заявку отменяли и собирали заново — другой номер на бумаге и
            потерянный порядок в очереди.

            Двигать можно только будущее. Вчерашний день отработан, смена
            закрыта и отчёт по ней сдан: перевесить эту работу на сегодня
            значит опустошить вчерашний отчёт и дописать в сегодняшний листы,
            которых сегодня никто не делал. Сервер это и не даст, а кнопку
            здесь не показываем вовсе. */}
        {data.plan_date && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 8, padding: '4px 10px', fontSize: 12.5,
          }}>
            <span style={{ color: 'var(--dim)' }}>{t('на день')}</span>
            {data.can_move_date ? (
              <input type="date" value={data.plan_date} min={todayISO()}
                disabled={busy}
                onChange={async (e) => {
                  const v = e.target.value;
                  if (!v || v === data.plan_date) return;
                  const d = await act('plan_date', { plan_date: v });
                  if (d) toast.success(
                    `Заявка перенесена на ${new Date(v).toLocaleDateString('ru-RU')}`);
                }}
                style={{
                  background: 'var(--card)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--text)', padding: '2px 6px',
                  fontSize: 12.5, fontFamily: 'inherit', outline: 'none',
                }} />
            ) : (
              <b style={{ color: 'var(--text)' }}>
                {new Date(data.plan_date).toLocaleDateString('ru-RU')}
              </b>
            )}
          </span>
        )}
        <Btn variant="primary" onClick={printBlank} style={{ marginLeft: 'auto' }}>
          🖨 {t('Печать A4')}
        </Btn>
      </div>

      {/* Ответственные в шапке бланка. Имена запоминаются: из смены в смену
          они те же, поэтому подсказка избавляет от ручного набора. */}
      <div className="no-print" style={{
        display: 'flex', gap: 10, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap',
      }}>
        {[
          { key: 'prod_head',      kind: 'prod_head',      label: 'Нач. производства' },
          { key: 'brigadier',      kind: 'brigadier',      label: 'Бригадир' },
          { key: 'warehouse_head', kind: 'warehouse_head', label: 'Завсклад' },
        ].map(fld => (
          <div key={fld.key} style={{ width: 200 }}>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 11, marginBottom: 3 }}>
              {t(fld.label)}
            </label>
            <SuggestInput
              kind={fld.kind}
              value={heads[fld.key]}
              onChange={v => setHeads(prev => ({ ...prev, [fld.key]: v }))}
              placeholder={t('Ф.И.О.')}
            />
          </div>
        ))}
        <Btn variant="ghost" onClick={saveHeads} disabled={saving}>
          {saving ? '…' : t('Сохранить')}
        </Btn>
      </div>

      {/* ── Правка заявки ────────────────────────────────────────────────
          Спрашивать смену не нужно: технолог правит когда угодно. Замок
          стоит на строках — выпущенное и то, что прессуют сейчас, закрыто. */}
      <div className="no-print" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        {data.is_cancelled ? (
          <>
            <span style={{ color: '#ef4444', fontSize: 13, fontWeight: 700 }}>
              ✖ {t('Заявка отменена')}
              {data.cancelled_by ? ` — ${data.cancelled_by}` : ''}
              {data.cancel_reason ? ` · ${data.cancel_reason}` : ''}
            </span>
            <Btn variant="ghost" onClick={restoreRequest} disabled={busy}>
              ↩ {t('Восстановить')}
            </Btn>
          </>
        ) : data.production_finished ? (
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>
            ✔ {t('Заявка полностью выпущена — изменение недоступно')}
          </span>
        ) : editing ? null : data.can_edit_now ? (
          <>
            <Btn variant="warning" onClick={startEdit}>✏ {t('Изменить')}</Btn>
            {data.locked_rows > 0 && (
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                🔒 {t('В работе')}: {data.locked_rows} — {t('эти позиции менять нельзя')}
              </span>
            )}
          </>
        ) : (
          <span style={{ color: 'var(--muted)', fontSize: 13 }}>
            🔒 {t('Все позиции уже в работе — изменение недоступно')}
          </span>
        )}

        {/* Отменить можно только нетронутую заявку. Документ не удаляется:
            остаётся в архиве, а позиции возвращаются в согласование. */}
        {data.can_cancel && !editing && (
          <Btn variant="danger" onClick={cancelRequest} disabled={busy}
            style={{ marginLeft: 'auto' }}>
            ✖ {t('Отменить заявку')}
          </Btn>
        )}
      </div>

      {editing && (
        <RequestEditForm
          data={data} draft={draft} setField={setField} busy={busy}
          onRemove={removeRow}
          onAdd={() => setAdding(true)}
          onSave={saveEdit}
          onCancel={() => setEditing(false)}
        />
      )}

      {/* Бланк — белый фон и чёрные линии, чтобы на бумаге читалось.
          Каждый пресс печатается своим листом: смена получает на руки
          только то, что делает именно она. */}
      <div id="req-print" style={{ display: editing ? 'none' : 'block', overflowX: 'auto' }}>
        {pressGroups.map(g => (
          <PrintSheet
            key={g.press}
            press={g.press}
            rows={g.rows}
            data={data}
            dateStr={dateStr}
            heads={heads}
          />
        ))}
      </div>

      {adding && (
        <AddRowModal
          busy={busy}
          onClose={() => setAdding(false)}
          onAdd={async (ids, pressId) => {
            const d = await act('add', { order_item_ids: ids, press_id: Number(pressId) });
            if (!d) return;
            // Новые строки тоже должны стать редактируемыми, не сбрасывая
            // то, что технолог уже вписал в остальные
            setDraft(prev => {
              const next = { ...prev };
              (d.rows || []).forEach(r => {
                if (r.can_edit && !next[r.id]) next[r.id] = {
                  plate_size: r.plate_size, note: r.note,
                  quantity_plan: String(r.quantity_plan),
                };
              });
              return next;
            });
            setAdding(false);
            toast.success(`${t('Добавлено позиций:')} ${ids.length}`);
          }}
        />
      )}
    </div>
  );
}

export function ProductionRequests() {
  const [list, setList]   = useState([]);
  const [date, setDate]   = useState('');
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId]   = useState(null);
  const [daily, setDaily]     = useState(false);
  const [blank, setBlank]     = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    productionAPI.getRequests(date ? { date } : {})
      .then(r => setList(r.data.results || []))
      .catch(() => toast.error(t('Не удалось загрузить заявки')))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  if (openId) return <RequestPrintView requestId={openId} onBack={() => { setOpenId(null); load(); }} />;
  if (daily)  return <DailyReportView onBack={() => setDaily(false)} />;
  if (blank)  return <ShiftBlankView onBack={() => setBlank(false)} />;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{
            padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)',
            borderRadius: 7, color: 'var(--text)', fontSize: 13,
          }}
        />
        {date && <Btn variant="ghost" onClick={() => setDate('')}>{t('✕ Сбросить')}</Btn>}
        <button onClick={load} style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--dim)', padding: '6px 12px', cursor: 'pointer' }}>🔄</button>
        {/* Общая картина за день: все заявки одним листом, по сменам и прессам */}
        <Btn variant="primary" onClick={() => setDaily(true)} style={{ marginLeft: 'auto' }}>
          📄 {t('Отчёт за день')}
        </Btn>
        {/* Тот же день с другой стороны: не что задали, а что пресс сделал и
            на каком режиме — сменный бланк вместо переписывания от руки */}
        <Btn variant="primary" onClick={() => setBlank(true)}>
          🖨 {t('Сменный бланк')}
        </Btn>
      </div>

      {loading ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>{t('Загрузка…')}</p>
      ) : list.length === 0 ? (
        <p style={{ color: 'var(--mid)', textAlign: 'center', padding: 24 }}>{t('Заявок нет')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map(r => (
            <div key={r.id}
              onClick={() => setOpenId(r.id)}
              style={{
                background: 'var(--bg)', borderRadius: 10, padding: '11px 14px',
                cursor: 'pointer', display: 'flex', alignItems: 'center',
                gap: 12, flexWrap: 'wrap',
                opacity: r.is_cancelled ? 0.55 : 1,
              }}>
              <span style={{
                color: 'var(--text)', fontWeight: 700,
                textDecoration: r.is_cancelled ? 'line-through' : 'none',
              }}>{r.number}</span>
              {r.is_cancelled && (
                <span title={r.cancel_reason} style={{
                  background: '#ef444418', color: '#ef4444', borderRadius: 5,
                  padding: '2px 9px', fontSize: 11, fontWeight: 700,
                }}>ОТМЕНЕНА</span>
              )}
              <span style={{
                background: r.shift === 'day' ? '#f59e0b18' : '#8b5cf618',
                color:      r.shift === 'day' ? '#f59e0b'   : '#8b5cf6',
                borderRadius: 5, padding: '2px 9px', fontSize: 11, fontWeight: 700,
              }}>{r.shift_display}</span>
              {/* Выписана заранее — это надо видеть в списке, а не открывая
                  каждую: работа лежит и ждёт своего дня, и пресс её до тех
                  пор не покажет. */}
              {r.plan_date && r.plan_date !== todayISO() && (
                <span style={{
                  background: '#0ea5e918', color: '#0284c7', borderRadius: 5,
                  padding: '2px 9px', fontSize: 11, fontWeight: 700,
                }}>📅 {t('на')} {new Date(r.plan_date).toLocaleDateString('ru-RU')}</span>
              )}
              <span style={{ color: 'var(--dim)', fontSize: 12 }}>
                {new Date(r.created_at).toLocaleString('ru')}
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                {t('поз.')} {r.items_count} · {t('план')} <b style={{ color: 'var(--text)' }}>{r.total_plan}</b> {t('шт')}
              </span>
              {r.brigadier && (
                <span style={{ color: 'var(--dim)', fontSize: 12 }}>👷 {r.brigadier}</span>
              )}
              <span style={{ color: 'var(--dim)', fontSize: 12, marginLeft: 'auto' }}>{r.author}</span>
              <span style={{ color: 'var(--accent)', fontSize: 13 }}>🖨</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductionRequestsPage() {
  return (
    <Layout title={t("Заявка на производство")}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)',
        borderRadius: 12, padding: 18,
      }}>
        <ProductionRequests />
      </div>
    </Layout>
  );
}
